import React, { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, Download } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// Round 22 · H1 / Round 23 · J3 — built-in PDF.js viewer. Bytes are fetched with the
// session auth header (api path) or from a blob: URL — never a token in a query string.
// On error only a plain message is shown (no URL / id / token) with a Download button.
// Test-ids: pdfjs-viewer pdfjs-canvas pdfjs-page pdfjs-prev pdfjs-next pdfjs-zoom-in pdfjs-zoom-out pdfjs-error pdfjs-download
export async function fetchPdfBytes(src) {
  const clean = String(src).split("#")[0];
  if (clean.startsWith("blob:") || clean.startsWith("data:")) {
    const r = await fetch(clean);
    return new Uint8Array(await r.arrayBuffer());
  }
  const r = await api.get(clean, { responseType: "arraybuffer", _silent: true });
  return new Uint8Array(r.data);
}

export function PdfJsViewer({ url, page = 1, className = "", testId = "pdfjs-viewer", downloadName = "document.pdf" }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [num, setNum] = useState(page || 1);
  const [scale, setScale] = useState(1.2);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const renderTask = useRef(null);
  const bytesRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setDoc(null); setErr(false); bytesRef.current = null;
    if (!url) return undefined;
    setBusy(true);
    let task = null;
    fetchPdfBytes(url)
      .then((bytes) => {
        if (cancelled) return null;
        bytesRef.current = bytes;
        task = pdfjs.getDocument({ data: bytes });
        return task.promise;
      })
      .then((d) => { if (!cancelled && d) { setDoc(d); setBusy(false); } })
      .catch(() => { if (!cancelled) { setErr(true); setBusy(false); } });
    return () => { cancelled = true; task?.destroy?.(); };
  }, [url]);
  useEffect(() => { setNum(page || 1); }, [page, url]);

  useEffect(() => {
    if (!doc || !canvasRef.current) return undefined;
    let cancelled = false;
    const p = Math.min(Math.max(1, num), doc.numPages);
    doc.getPage(p).then((pg) => {
      if (cancelled) return;
      const vp = pg.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = vp.width; canvas.height = vp.height;
      renderTask.current?.cancel?.();
      renderTask.current = pg.render({ canvasContext: canvas.getContext("2d"), viewport: vp });
      renderTask.current.promise.catch(() => {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [doc, num, scale]);

  const download = async () => {
    try {
      const bytes = bytesRef.current || await fetchPdfBytes(url);
      const href = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = href; a.download = downloadName; a.click();
      URL.revokeObjectURL(href);
    } catch { setErr(true); }
  };

  return (
    <div className={`flex h-full flex-col ${className}`} data-testid={testId}>
      <div className="flex items-center gap-1 border-b bg-slate-50 px-2 py-1 text-xs">
        <button type="button" className="rounded p-1 hover:bg-slate-200 disabled:opacity-40" disabled={!doc || num <= 1} onClick={() => setNum((n) => n - 1)} data-testid="pdfjs-prev"><ChevronLeft className="h-4 w-4" /></button>
        <span className="tabular-nums" data-testid="pdfjs-page">{doc ? `${Math.min(num, doc.numPages)} / ${doc.numPages}` : "—"}</span>
        <button type="button" className="rounded p-1 hover:bg-slate-200 disabled:opacity-40" disabled={!doc || num >= (doc?.numPages || 1)} onClick={() => setNum((n) => n + 1)} data-testid="pdfjs-next"><ChevronRight className="h-4 w-4" /></button>
        <span className="ms-auto" />
        <button type="button" className="rounded p-1 hover:bg-slate-200" onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} data-testid="pdfjs-zoom-out"><ZoomOut className="h-4 w-4" /></button>
        <span className="tabular-nums">{Math.round(scale * 100)}%</span>
        <button type="button" className="rounded p-1 hover:bg-slate-200" onClick={() => setScale((s) => Math.min(3, s + 0.2))} data-testid="pdfjs-zoom-in"><ZoomIn className="h-4 w-4" /></button>
        <button type="button" className="ms-1 rounded p-1 hover:bg-slate-200" title={t("pdfViewer.download")} onClick={download} data-testid="pdfjs-download"><Download className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-auto bg-slate-100 p-2">
        {busy && <div className="flex items-center justify-center p-6 text-xs text-muted-foreground"><Loader2 className="me-1 h-4 w-4 animate-spin" /></div>}
        {err && (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-slate-700" data-testid="pdfjs-error">
            <p>{t("pdfViewer.failed")}</p>
            <button type="button" className="inline-flex items-center gap-1 rounded border bg-white px-3 py-1 text-xs hover:bg-slate-50" onClick={download} data-testid="pdfjs-error-download">
              <Download className="h-3.5 w-3.5" />{t("pdfViewer.download")}
            </button>
          </div>
        )}
        <canvas ref={canvasRef} className={`mx-auto block shadow ${err ? "hidden" : ""}`} data-testid="pdfjs-canvas" />
      </div>
    </div>
  );
}

export function pageFromUrl(url) {
  const m = /#page=(\d+)/.exec(url || "");
  return m ? Number(m[1]) : 1;
}
